import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import axios from "axios";
import apiClient from "../common/apiClient";
import { toast } from "react-toastify";
import { Check, ShoppingCart, Info, Shield, Star, X, CreditCard, ArrowRight, History, Zap, MapPin, AlertTriangle } from "lucide-react";
import { PLAN_ADDONS, VERIFY_LISTING_PRICING, DEFAULT_VERIFY_LISTING_PLAN_CODE } from "../constants/feature.constants";
import MyPlans from "../components/MyPlans";
import Addons from "../components/Addons";
import PaymentCelebration from "../components/PaymentCelebration";
import PaymentProcessingOverlay from "../components/PaymentProcessingOverlay";
import OfferBanner from "../components/offers/OfferBanner";
import OfferBadge from "../components/offers/OfferBadge";
import OfferTermsSheet from "../components/offers/OfferTermsSheet";
import PricingOfferDetails from "../components/offers/PricingOfferDetails";
import { fetchOfferQuote, getClockOffset } from "../lib/offers/offerApi";
import { resolveOfferTheme, getOfferLabels, formatMonths, formatBonusMonths } from "../lib/offers/offerTheme";
import { useUserProfile } from "../contexts/UserProfileContext";
import {
  trackPlansViewed,
  trackInitiateCheckout,
  trackAddPaymentInfo,
  trackPurchase,
  trackCouponApplied,
  trackPaymentFailed,
} from "../utils/metaPixel";

// Helper to load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// Each step of the funnel gets its own URL so Meta / GA can target it with a
// plain "URL contains" rule instead of a fragile DOM click selector.
const SUBSCRIPTION_ROUTES = {
  plans: "/subscription",
  checkout: "/subscription/checkout",
  success: "/subscription/success",
  history: "/subscription/history",
};

const CITY_LIST_API = "/ijscui/probroker-admin/ceknvuhbd/PremiumPlan/protected/city/all";

// The city API carries each city's state; collect the distinct state names for
// the Billing State dropdown. Payload nesting varies, so accept each known shape.
const extractStateNames = (payload) => {
  const list =
    [payload?.data?.docs, payload?.data, payload?.docs, payload].find((c) => Array.isArray(c)) || [];

  const states = list
    .map((item) => item?.State || item?.state?.name || item?.stateName || item?.state || "")
    .map((name) => String(name).trim())
    .filter(Boolean);

  return [...new Set(states)].sort();
};

// setTimeout overflows past ~24.8 days; a longer wait just re-checks early.
const MAX_TIMEOUT_MS = 2147483647;
// Re-fetch a moment after an offer ends so the server has already dropped it.
const OFFER_END_REFETCH_DELAY_MS = 2000;

// "6 & 12" for the months a plan's offer rows cover.
const joinDurations = (rows, conjunction = "&") => {
  const months = rows.map((row) => Number(row?.durationMonths)).filter(Boolean).sort((a, b) => a - b);
  if (months.length <= 1) return String(months[0] || "");
  return `${months.slice(0, -1).join(", ")} ${conjunction} ${months[months.length - 1]}`;
};

const viewFromPath = (pathname = "") => {
  if (pathname.startsWith(SUBSCRIPTION_ROUTES.checkout)) return "checkout";
  if (pathname.startsWith(SUBSCRIPTION_ROUTES.success)) return "success";
  if (pathname.startsWith(SUBSCRIPTION_ROUTES.history)) return "history";
  return "plans";
};

const Subscription = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyTab, setHistoryTab] = useState("subscriptions");

  const navigate = useNavigate();
  const location = useLocation();

  // `view` is read from the URL rather than kept in state, so Back/Forward,
  // refresh and shared links all land on the right step.
  const view = viewFromPath(location.pathname);
  const setView = useCallback(
    (nextView, options) => {
      const path = SUBSCRIPTION_ROUTES[nextView] || SUBSCRIPTION_ROUTES.plans;
      if (location.pathname !== path) navigate(path, options);
    },
    [navigate, location.pathname]
  );
  const [activeTab, setActiveTab] = useState("plans"); // New state for "Plans" vs "Addons"
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [checkoutCoupon, setCheckoutCoupon] = useState("");
  const [appliedCheckoutCoupon, setAppliedCheckoutCoupon] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [subUserCount, setSubUserCount] = useState(1);
  const [verifyListingPlanCode, setVerifyListingPlanCode] = useState(DEFAULT_VERIFY_LISTING_PLAN_CODE);
  const [paymentCelebration, setPaymentCelebration] = useState(null);
  // Between "Razorpay says paid" and "server confirmed the plan" (see finalizePayment).
  const [paymentProgress, setPaymentProgress] = useState(null);
  // Campaign offers come with the plans listing; all of it is display-only.
  const [activeOffers, setActiveOffers] = useState([]);
  const [offerClockOffset, setOfferClockOffset] = useState(0);
  const [offerQuote, setOfferQuote] = useState(null);
  const [isOfferTermsOpen, setIsOfferTermsOpen] = useState(false);
  const [planStartDate, setPlanStartDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [userGSTNo, setUserGSTNo] = useState("");
  const [billingName, setBillingName] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [billingState, setBillingState] = useState("");
  const [billingStateOptions, setBillingStateOptions] = useState([]);
  const hasForcedProfileRefresh = useRef(false);
  const hasPrefilledGSTNo = useRef(false);

  const userId = Cookies.get("userId");
  const token = Cookies.get("token");
  const { userProfile, loadingProfile, fetchUserProfile } = useUserProfile();

  const featureAccessRecords = useMemo(() => {
    if (Array.isArray(userProfile?.FeaturesAccessData)) {
      return userProfile.FeaturesAccessData;
    }

    if (Array.isArray(userProfile?.user?.FeaturesAccessData)) {
      return userProfile.user.FeaturesAccessData;
    }

    return [];
  }, [userProfile]);

  const activePlan = useMemo(() => {
    if (!userProfile?.paymentHistory || !Array.isArray(userProfile.paymentHistory)) return null;

    // 1. Try to find explicit "Active" plan
    let plan = userProfile.paymentHistory.find(p => p.status === "Active");

    // 2. If not found, find the latest one with isPending === false
    if (!plan) {
      const nonPendingPlans = userProfile.paymentHistory
        .filter(p => p.isPending === false)
        .sort((a, b) => {
          const dateA = new Date(a.createdOn || a.startDate || a.createdAt || 0);
          const dateB = new Date(b.createdOn || b.startDate || b.createdAt || 0);
          return dateB - dateA;
        });

      if (nonPendingPlans.length > 0) {
        plan = nonPendingPlans[0];
      }
    }

    return plan;
  }, [userProfile?.paymentHistory]);

  const activePlanCardId = useMemo(() => {
    if (!activePlan) return "";

    if (typeof activePlan?.planId === "string") {
      return activePlan.planId;
    }

    if (activePlan?.planId && typeof activePlan.planId === "object") {
      return String(activePlan.planId?._id || activePlan.planId?.id || "");
    }

    return "";
  }, [activePlan]);

  const activePlanExpiryDate = useMemo(() => {
    // 1. Prefer top-level PlanExpriDate from profile API when available.
    if (userProfile?.PlanExpriDate) {
      return String(userProfile.PlanExpriDate).split("T")[0];
    }

    // 2. Check activePlan (from paymentHistory)
    if (activePlan?.expiredDate) return String(activePlan.expiredDate).split("T")[0];

    // 3. Check paidAddonsData (specifically EndDate as requested)
    if (Array.isArray(userProfile?.paidAddonsData) && userProfile.paidAddonsData.length > 0) {
      // Find the first active addon or just use the latest one
      const activeAddon = userProfile.paidAddonsData.find(a => a.AddonStatus === "Active" || a.status === "Active");
      const target = activeAddon || userProfile.paidAddonsData[0];
      if (target?.EndDate || target?.expiredDate) {
        return String(target.EndDate || target.expiredDate).split("T")[0];
      }
    }

    return "";
  }, [activePlan, userProfile]);

  const profilePlanName = useMemo(() => {
    return (
      userProfile?.user?.plan ||
      userProfile?.plan ||
      userProfile?.PlanName ||
      userProfile?.user?.PlanName ||
      activePlan?.PlanName ||
      activePlan?.planId?.name ||
      activePlan?.planId?.planName ||
      ""
    );
  }, [activePlan, userProfile]);

  const isDemoAccount = useMemo(() => {
    // If user has payment history, they are NOT a demo account
    if (Array.isArray(userProfile?.paymentHistory) && userProfile.paymentHistory.length > 0) {
      return false;
    }

    const planType = String(
      userProfile?.plan ||
      userProfile?.user?.plan ||
      profilePlanName ||
      ""
    ).trim().toLowerCase();

    return planType === "demo account" || planType === "demo_account";
  }, [profilePlanName, userProfile]);

  const demoPlanExpiryDate = useMemo(() => {
    const rawExpiryDate =
      userProfile?.DemoPlanExpriDate ||
      userProfile?.DemoPlanExpriedDate ||
      userProfile?.user?.DemoPlanExpriDate ||
      userProfile?.user?.DemoPlanExpriedDate;

    if (!rawExpiryDate) return "";

    return String(rawExpiryDate).split("T")[0];
  }, [userProfile]);

  const currentPlanName = isDemoAccount 
    ? (profilePlanName || "Demo Account") 
    : (profilePlanName || (activePlan ? (activePlan.PlanName || activePlan.planId?.name || "Premium Plan") : "No Active Plan"));
  const currentPlanExpiryDate = isDemoAccount ? demoPlanExpiryDate : activePlanExpiryDate;

  useEffect(() => {
    if (hasPrefilledGSTNo.current) {
      return;
    }

    const profileGSTNo = String(
      userProfile?.UserGSTNO ||
      userProfile?.user?.UserGSTNO ||
      ""
    ).trim();

    if (profileGSTNo) {
      setUserGSTNo(profileGSTNo);
      hasPrefilledGSTNo.current = true;
    }
  }, [userProfile?.UserGSTNO, userProfile?.user?.UserGSTNO]);

  // Populate the Billing State dropdown from the admin city list.
  useEffect(() => {
    let cancelled = false;

    apiClient("get", CITY_LIST_API).then((res) => {
      if (cancelled || !res.success) return;
      setBillingStateOptions(extractStateNames(res.data));
    });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!userId || !token || hasForcedProfileRefresh.current) {
      return;
    }

    hasForcedProfileRefresh.current = true;
    fetchUserProfile(userId, true).catch((error) => {
      console.warn("Subscription profile refresh failed:", error?.message);
    });
  }, [userId, token, fetchUserProfile]);

  const minSelectableStartDate = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    
    // Only consider PlanExpriDate, NOT DemoPlanExpriedDate
    let planExpiryToUse = "";
    if (userProfile?.PlanExpriDate) {
      planExpiryToUse = String(userProfile.PlanExpriDate).split("T")[0];
    } else if (activePlan?.expiredDate && !isDemoAccount) {
      // Also consider active plan's expiry date only if it's not a demo account
      planExpiryToUse = String(activePlan.expiredDate).split("T")[0];
    }

    if (!planExpiryToUse) return today;

    const [year, month, day] = planExpiryToUse.split("-").map(Number);
    if (!year || !month || !day) return today;

    const nextDate = new Date(Date.UTC(year, month - 1, day));
    nextDate.setUTCDate(nextDate.getUTCDate() + 1);
    const dayAfterExpiry = nextDate.toISOString().split("T")[0];

    return dayAfterExpiry > today ? dayAfterExpiry : today;
  }, [activePlan, userProfile?.PlanExpriDate, isDemoAccount]);

  useEffect(() => {
    if (view !== "checkout") return;
    if (!minSelectableStartDate) return;

    if (!planStartDate || planStartDate < minSelectableStartDate) {
      setPlanStartDate(minSelectableStartDate);
    }
  }, [view, planStartDate, minSelectableStartDate]);

  const latestPaidAccount = (userProfile?.paymentHistory || [])
    .slice()
    .sort((a, b) => new Date(b?.createdOn || b?.createdAt || 0) - new Date(a?.createdOn || a?.createdAt || 0))[0];
  const mappedSubscriptionHistory = useMemo(() => {
    const paymentHistory = userProfile?.paymentHistory || [];
    const featuresAccessData = userProfile?.FeaturesAccessData || [];

    return paymentHistory.map((payment) => {
      const accessIds = new Set(Array.isArray(payment?.FeaturesAccess) ? payment.FeaturesAccess : []);
      const resolvedFeatureNames = featuresAccessData
        .filter((featureAccess) => accessIds.has(featureAccess?._id))
        .map((featureAccess) => featureAccess?.FeaturesName)
        .filter(Boolean);

      return {
        ...payment,
        features: resolvedFeatureNames.length > 0
          ? resolvedFeatureNames
          : (Array.isArray(payment?.features) ? payment.features : []),
      };
    });
  }, [userProfile?.paymentHistory, userProfile?.FeaturesAccessData]);

  const normalizeAddonCode = (value) =>
    String(value || "")
      .toLowerCase()
      .trim();

  const isDemoPlan = (plan) => {
    const name = String(plan?.planName || "").toLowerCase();
    return name.includes("demo") || name.includes("demo account") || name.includes("demoaccount");
  };

  const orderedPlans = useMemo(() => {
    if (!Array.isArray(plans)) return [];

    // Check if user has any payment history to hide demo plans
    const hasPaymentHistory =
      Array.isArray(userProfile?.paymentHistory) &&
      userProfile.paymentHistory.length > 0;

    const demos = plans.filter((p) => isDemoPlan(p));
    const others = plans.filter((p) => !isDemoPlan(p));

    if (hasPaymentHistory) {
      return others;
    }

    return [...demos, ...others];
  }, [plans, userProfile?.paymentHistory]);

  const allowedPlanAddonCodes = new Set(
    Object.values(PLAN_ADDONS).map((code) => normalizeAddonCode(code))
  );

  const isPlanAddonAllowed = (addonCode) => allowedPlanAddonCodes.has(normalizeAddonCode(addonCode));

  // Helper to check if an addon is for sub-users
  const isSubUserAddon = (addon) => {
    const subUserCode = normalizeAddonCode(PLAN_ADDONS.SUBUSER_LIMIT);
    const addonCode = normalizeAddonCode(addon.code);

    return addonCode === subUserCode || addon.type === "limit";
  };

  // Helper to check if an addon is the Verified Listings add-on
  const isVerifiedListingAddon = (addon) =>
    normalizeAddonCode(addon?.code) === normalizeAddonCode(PLAN_ADDONS.VERIFIED_LISTING);

  // Verified Listings is sold as a credit pack, so its cost comes from the selected
  // pack rather than from addon.price x plan duration.
  const getVerifyListingPack = () =>
    VERIFY_LISTING_PRICING[verifyListingPlanCode] ||
    VERIFY_LISTING_PRICING[DEFAULT_VERIFY_LISTING_PLAN_CODE];

  const fetchPlans = useCallback(async () => {
    try {
      if (!userId) {
        toast.error("User not logged in");
        setLoading(false);
        return;
      }
      const response = await apiClient(
        "get",
        `/ijscui/probroker-admin/ceknvuhbd/PremiumPlan/protected/plans/user/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.success) {
        const data = response.data?.data;
        setPlans(data?.docs || []);
        // Missing when the user has no city; the plans then simply show no offer.
        setActiveOffers(Array.isArray(data?.activeOffers) ? data.activeOffers : []);
        setOfferClockOffset(getClockOffset(data?.serverTime));
      } else {
        toast.error(response.error?.message || "Failed to fetch plans");
      }
    } catch {
      toast.error("An error occurred while fetching plans");
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // A re-fetch hands back new plan objects; keep the cart pointing at them so
  // an offer that ended (or started) shows up in checkout too.
  useEffect(() => {
    if (!selectedPlan) return;
    const freshPlan = plans.find((plan) => plan._id === selectedPlan._id);
    if (!freshPlan || freshPlan === selectedPlan) return;
    setSelectedPlan(freshPlan);
    const freshPricing = (freshPlan.pricing || []).find((row) => row._id === selectedPricing?._id);
    if (freshPricing) setSelectedPricing(freshPricing);
  }, [plans, selectedPlan, selectedPricing?._id]);

  // Re-fetch when the soonest live offer ends, so banners and badges go away
  // without a reload. The server decides; this only asks again at the right time.
  const nextOfferEndMs = useMemo(() => {
    const endTimes = [
      ...activeOffers.map((offer) => offer?.endAt),
      ...plans.flatMap((plan) => (plan.pricing || []).map((row) => row?.offer?.endAt)),
    ]
      .map((value) => new Date(value).getTime())
      .filter(Number.isFinite);
    return endTimes.length > 0 ? Math.min(...endTimes) : null;
  }, [activeOffers, plans]);

  useEffect(() => {
    if (!nextOfferEndMs) return undefined;
    const delay = nextOfferEndMs - (Date.now() + offerClockOffset) + OFFER_END_REFETCH_DELAY_MS;
    if (delay <= 0) return undefined;
    const timer = window.setTimeout(fetchPlans, Math.min(delay, MAX_TIMEOUT_MS));
    return () => window.clearTimeout(timer);
  }, [nextOfferEndMs, offerClockOffset, fetchPlans]);

  // Checkout bonus line. Asked again whenever the duration or promo code
  // changes; only rows the listing marked with an offer need a quote.
  useEffect(() => {
    setOfferQuote(null);
    if (view !== "checkout" || !userId || !selectedPlan?._id || !selectedPricing?.offer) return undefined;

    let cancelled = false;
    fetchOfferQuote({
      userId,
      planId: selectedPlan._id,
      durationInMonth: selectedPricing.durationMonths,
      promocode: appliedCheckoutCoupon?.code,
      token,
    })
      .then((quote) => {
        if (!cancelled) setOfferQuote(quote);
      })
      .catch(() => {
        if (!cancelled) setOfferQuote(null);
      });

    return () => {
      cancelled = true;
    };
  }, [
    view,
    userId,
    token,
    selectedPlan?._id,
    selectedPricing?.offer,
    selectedPricing?.durationMonths,
    appliedCheckoutCoupon?.code,
  ]);

  // Pricing rows carry a compact offer summary; banner colours, artwork and
  // terms live on the full offer from `activeOffers`.
  const findActiveOffer = (pricingOffer) =>
    pricingOffer
      ? activeOffers.find(
        (offer) =>
          String(offer?.offerId || "") === String(pricingOffer.offerId || "") ||
          (offer?.slug && offer.slug === pricingOffer.slug)
      ) || null
      : null;

  const getPricingOfferTheme = (pricingOffer) => {
    const fullOffer = findActiveOffer(pricingOffer);
    return resolveOfferTheme({
      ...(fullOffer?.display || {}),
      theme: pricingOffer?.theme || fullOffer?.display?.theme,
    });
  };

  const handleOfferBannerCta = () => {
    setActiveTab("plans");
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    document
      .getElementById("subscription-plan-grid")
      ?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  };

  const handleViewMyPlanClick = async () => {
    setView("history");
    setHistoryTab("subscriptions");

    if (!userId || !token) {
      return;
    }

    try {
      // Use the centralized fetch function from context
      await fetchUserProfile(userId, true);
    } catch (error) {
      console.warn("View My Plan refresh failed:", error?.message);
    }
  };

  const handleSelectAddonsTab = async () => {
    setActiveTab("addons");

    if (!userId || !token) {
      return;
    }

    try {
      await fetchUserProfile(userId, true);
    } catch (error) {
      console.warn("Addons tab profile refresh failed:", error?.message);
    }
  };

  const handleHistoryTabChange = async (tab) => {
    setHistoryTab(tab);
    if (tab === "addons" && userId) {
      try {
        await fetchUserProfile(userId, true);
      } catch (error) {
        console.warn("Addons tab refresh failed:", error?.message);
      }
    }
  };

  // A refresh or a shared link can drop someone straight onto /checkout or
  // /success with no plan in memory - send them back to the plans list instead
  // of rendering a half-built cart.
  useEffect(() => {
    if (view === "checkout" && !selectedPlan) {
      navigate(SUBSCRIPTION_ROUTES.plans, { replace: true });
      return;
    }
    if (view === "success" && !paymentCelebration) {
      navigate(SUBSCRIPTION_ROUTES.plans, { replace: true });
    }
  }, [view, selectedPlan, paymentCelebration, navigate]);

  // Meta: /subscription reached.
  useEffect(() => {
    if (view === "plans") trackPlansViewed();
  }, [view]);

  const handleChoosePlan = (plan) => {
    const defaultPricing = plan?.pricing?.[0];
    if (!defaultPricing) {
      toast.error("No pricing available for this plan");
      return;
    }
    setSelectedPlan(plan);
    setSelectedPricing(defaultPricing);
    setPlanStartDate(minSelectableStartDate);

    // Meta: "Choose Plan" clicked -> /subscription/checkout
    trackInitiateCheckout({
      planName: plan?.planName,
      planId: plan?._id,
      months: defaultPricing?.durationMonths,
      amount: defaultPricing?.price,
    });

    setView("checkout");
    window.scrollTo(0, 0);
  };

  const handleAddonToggle = (addon) => {
    setSelectedAddons((prev) => {
      const isSelected = prev.some((a) => a._id === addon._id);
      if (isSelected) {
        if (isSubUserAddon(addon)) {
          setSubUserCount(1);
        }
        if (isVerifiedListingAddon(addon)) {
          setVerifyListingPlanCode(DEFAULT_VERIFY_LISTING_PLAN_CODE);
        }
        return prev.filter((a) => a._id !== addon._id);
      } else {
        return [...prev, addon];
      }
    });
  };

  const calculateAddonTotal = (addons, subCount, planMonths = 1) => {
    return addons.reduce((total, addon) => {
      if (isSubUserAddon(addon)) {
        return total + addon.price * subCount;
      }
      if (isVerifiedListingAddon(addon)) {
        return total + getVerifyListingPack().amount;
      }
      return total + addon.price * planMonths;
    }, 0);
  };

  // Cost of a single addon line as shown on the card and in the order summary.
  const getAddonLineAmount = (addon, subCount, planMonths = 1) => {
    if (isSubUserAddon(addon)) {
      return addon.price * subCount;
    }
    if (isVerifiedListingAddon(addon)) {
      return getVerifyListingPack().amount;
    }
    return addon.price * planMonths;
  };

  const getAddonActivationValue = (addon, subCount, planMonths = 1) => {
    if (isSubUserAddon(addon)) {
      return Number(subCount);
    }

    if (isVerifiedListingAddon(addon)) {
      return Number(getVerifyListingPack().credits);
    }

    const baseValue = Number(addon?.value || 0);
    if (!baseValue) {
      return null;
    }

    return baseValue * Number(planMonths || 1);
  };

  const calculateExpiredDate = (startDateValue, durationMonths) => {
    if (!startDateValue) return null;

    const [year, month, day] = String(startDateValue).split("-").map(Number);
    if (!year || !month || !day) return null;

    const end = new Date(Date.UTC(year, month - 1, day));
    end.setUTCMonth(end.getUTCMonth() + Number(durationMonths || 1));
    end.setUTCDate(end.getUTCDate() - 1);
    return end;
  };

  const toUtcMidnightIso = (dateValue) => {
    if (!dateValue) return "";
    return `${dateValue}T00:00:00.000Z`;
  };

  const handleApplyCoupon = () => {
    if (!selectedPlan || !checkoutCoupon) return;
    const promoCodes = selectedPlan.promoCodes || [];
    const found = promoCodes.find(
      (pc) => pc.code.toLowerCase() === checkoutCoupon.toLowerCase()
    );
    if (found) {
      if (found.status === "Active") {
        setAppliedCheckoutCoupon(found);
        trackCouponApplied({
          code: found.code,
          discount: found.discountValue,
          planName: selectedPlan?.planName,
        });
        toast.success("Coupon applied successfully!");
      } else if (found.status === "Expired") {
        toast.error("This coupon code is Expired");
        setAppliedCheckoutCoupon(null);
      } else {
        toast.error("This coupon code is not available");
        setAppliedCheckoutCoupon(null);
      }
    } else {
      toast.error("Invalid coupon code");
      setAppliedCheckoutCoupon(null);
    }
  };

  const handleActivatePlan = async () => {
    if (!selectedPlan || !selectedPricing || !userId) {
      toast.error("Please select a plan and duration");
      return;
    }

    if (!planStartDate) {
      toast.error("Please select a start date");
      return;
    }

    if (!billingName.trim()) {
      toast.error("Please enter Billing Name");
      return;
    }

    if (!billingEmail.trim()) {
      toast.error("Please enter Billing Email");
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(billingEmail.trim())) {
      toast.error("Please enter a valid Billing Email");
      return;
    }

    if (!billingAddress.trim()) {
      toast.error("Please enter Billing Address");
      return;
    }

    if (!billingState.trim()) {
      toast.error("Please select Billing State");
      return;
    }

    if (planStartDate < minSelectableStartDate) {
      const expiryText = activePlanExpiryDate
        ? new Date(`${activePlanExpiryDate}T00:00:00`).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        : "your current plan date";

      toast.warning(`Your plan is currently active and expires on ${expiryText}. Please select a date after that.`);
      return;
    }

    const calculatedExpiredDate = calculateExpiredDate(planStartDate, selectedPricing?.durationMonths);
    if (!calculatedExpiredDate) {
      toast.error("Invalid start date selected");
      return;
    }

    const startDateIso = toUtcMidnightIso(planStartDate);
    const expiredDateValue = calculatedExpiredDate.toISOString().split("T")[0];

    if (!expiredDateValue) {
      toast.error("Could not calculate expiry date");
      return;
    }

    const expiredDateIso = toUtcMidnightIso(expiredDateValue);

    const TAX_RATE = 0.18;
    const basePrice = selectedPricing?.price || 0;
    const appliedPromocode = appliedCheckoutCoupon?.code || "";

    const planMonths = Number(selectedPricing?.durationMonths || 1);
    const addonPrice = calculateAddonTotal(selectedAddons, subUserCount, planMonths);

    let discountAmount = 0;
    if (appliedCheckoutCoupon) {
      if (appliedCheckoutCoupon.discountType === "percentage") {
        // Keep discount logic consistent with checkout summary.
        discountAmount = ((basePrice + addonPrice) * appliedCheckoutCoupon.discountValue) / 100;
      } else {
        discountAmount = appliedCheckoutCoupon.discountValue;
      }
    }
    // Round discount to 2 decimals
    discountAmount = Number((Number(discountAmount || 0)).toFixed(2));

    // Compute subtotal, tax and final total with two-decimal precision
    const subtotalAfterDiscount = Number((basePrice - discountAmount + addonPrice).toFixed(2));
    const taxAmount = Number((subtotalAfterDiscount * TAX_RATE).toFixed(2));
    const finalTotal = Number(Math.max(Number((subtotalAfterDiscount + taxAmount).toFixed(2)), 0));

    const isLoaded = await loadRazorpayScript();
    if (!isLoaded) {
      toast.error("Razorpay SDK failed to load. Are you online?");
      return;
    }

    try {
      const orderResponse = await axios.post(
        `${process.env.REACT_APP_API_IP || ""}/ijscui/probroker-admin/ceknvuhbd/PremiumPlan/protected/payment/order`,
        { amount: Number(finalTotal.toFixed(2)), currency: "INR" }
      );

      if (!orderResponse.data?.order?.id) {
        toast.error("Failed to create Razorpay order");
        return;
      }

      const orderId = orderResponse.data.order.id;

      console.log("REACT_APP_RAZORPAY_KEY_ID:", process.env.REACT_APP_RAZORPAY_KEY_ID);

      if (!process.env.REACT_APP_RAZORPAY_KEY_ID || process.env.REACT_APP_RAZORPAY_KEY_ID === "YOUR_RAZORPAY_KEY_ID") {
        toast.error("Razorpay key is missing or not configured.");
        return;
      }

      // Meta: Razorpay checkout about to open.
      trackAddPaymentInfo({
        planName: selectedPlan?.planName,
        planId: selectedPlan?._id,
        months: selectedPricing?.durationMonths,
        amount: finalTotal,
      });

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: Math.round(finalTotal * 100),
        currency: "INR",
        name: "PRObroker",
        description: `Purchase ${selectedPlan.planName}`,
        order_id: orderId,
        handler: (response) => finalizePayment(response),
        prefill: { name: "", email: "", contact: "" },
        theme: { color: "#7c3aed" },
      };

      // Runs after Razorpay reports success: verify, then activate. The overlay
      // stays up the whole time so the user always knows where things stand.
      // A retry resumes from the step that failed; both calls are safe to repeat
      // because the server matches them to the same Razorpay order.
      async function finalizePayment(response, previousVerifyResponse = null) {
        const progressBase = {
          amount: finalTotal,
          paymentId: response.razorpay_payment_id,
          planName: selectedPlan?.planName,
        };
        const failWith = (message, verifiedResponse = null) => {
          setPaymentProgress({
            ...progressBase,
            stage: "error",
            message,
            planMayBeActive: Boolean(verifiedResponse),
            retry: () => finalizePayment(response, verifiedResponse),
          });
        };

        setPaymentProgress({ ...progressBase, stage: previousVerifyResponse ? "activating" : "verifying" });

          try {
            const hasSubUserAddon = selectedAddons.some(a => isSubUserAddon(a));
            const totalSubUserLimit = selectedPlan.subUserLimit + (hasSubUserAddon ? subUserCount : 0);
            const planMonths = Number(selectedPricing?.durationMonths || 1);
            const ownerMonthlyContactLimit = Number(
              selectedPlan?.OwnerTotalContactLimit ?? selectedPlan?.ownerTotalContactLimit ?? 0
            );
            const builderMonthlyContactLimit = Number(
              selectedPlan?.BuilderTotalContactLimit ?? selectedPlan?.builderTotalContactLimit ?? 0
            );
            const brokerMonthlyContactLimit = Number(
              selectedPlan?.BrokerTotalContactLimit ?? selectedPlan?.brokerTotalContactLimit ?? 0
            );
            const requirementMonthlyContactLimit = Number(
              selectedPlan?.RequirementTotalContactLimit ?? selectedPlan?.requirementTotalContactLimit ?? selectedPlan?.requirementContactLimit ?? 0
            );
            const ownerTotalContactLimit = ownerMonthlyContactLimit * planMonths;
            const builderTotalContactLimit = builderMonthlyContactLimit * planMonths;
            const brokerTotalContactLimit = brokerMonthlyContactLimit * planMonths;
            const requirementTotalContactLimit = requirementMonthlyContactLimit * planMonths;

            const verifyPayload = {
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
              promocode: appliedPromocode,
                  // durationInMonth / expiredDate stay the PAID term: the server adds
                  // any offer bonus itself. promocode is repeated inside planData
                  // because that is what activation reads, so an offer that does not
                  // stack with promo codes is withheld exactly as the quote said.
                  planData: {
                  userId,
                  UserGSTNO: userGSTNo,
                  planId: selectedPlan._id,
                  startDate: startDateIso,
                  expiredDate: expiredDateIso,
                  durationInMonth: selectedPricing.durationMonths,
                  promocode: appliedPromocode,
                  amount: finalTotal,
                  taxAmount: taxAmount,
                  limit: selectedPlan.dailyContactLimit,
                  subUserLimit: totalSubUserLimit,
                  OwnerTotalContactLimit: ownerTotalContactLimit,
                  BuilderTotalContactLimit: builderTotalContactLimit,
                  BrokerTotalContactLimit: brokerTotalContactLimit,
                  RequirementTotalContactLimit: requirementTotalContactLimit,
                },
            };

            let verifyResponse = previousVerifyResponse;
            if (!verifyResponse) {
              try {
                verifyResponse = await axios.post(
                  `${process.env.REACT_APP_API_IP || ""}/ijscui/probroker-admin/ceknvuhbd/PremiumPlan/protected/payment/verify`,
                  verifyPayload
                );
              } catch (verifyError) {
                console.error("Verification error:", verifyError);
                failWith(verifyError?.response?.data?.message || "We couldn't reach the server to verify your payment.");
                return;
              }
            }

            if (verifyResponse.data?.success) {
              setPaymentProgress({ ...progressBase, stage: "activating" });
              const validSelectedAddons = selectedAddons.filter((addon) => isPlanAddonAllowed(addon?.code));

              // Verified Listings is activated from the same combined Razorpay payment, so
              // forward that payment's references per selected add-on. Matches the payload
              // the standalone VerifyListing/payment/verify endpoint expects.
              const verifyListingItems = validSelectedAddons
                .filter((addon) => isVerifiedListingAddon(addon))
                .map(() => ({
                  userId,
                  planCode: verifyListingPlanCode,
                  orderId: response.razorpay_order_id,
                  paymentId: response.razorpay_payment_id,
                  signature: response.razorpay_signature,
                }));

              const activationPayload = {
                userId,
                UserGSTNO: userGSTNo,
                BillingName: billingName,
                GSTNumber: gstNumber,
                BillingAddress: billingAddress,
                BillingEmail: billingEmail,
                BillingState: billingState,
                planId: selectedPlan._id,
                startDate: startDateIso,
                expiredDate: expiredDateIso,
                limit: selectedPlan.dailyContactLimit,
                totalContactLimit: selectedPlan.totalContactLimit,
                subUserLimit: totalSubUserLimit,
                OwnerTotalContactLimit: ownerTotalContactLimit,
                BuilderTotalContactLimit: builderTotalContactLimit,
                BrokerTotalContactLimit: brokerTotalContactLimit,
                RequirementTotalContactLimit: requirementTotalContactLimit,
                features: selectedPlan.features,
                durationInMonth: selectedPricing.durationMonths,
                amount: finalTotal.toString(),
                taxAmount: taxAmount,
                promocode: appliedPromocode,
                discountAmount: discountAmount,
                originalPrice: basePrice,
                addonPrice: calculateAddonTotal(validSelectedAddons, subUserCount, planMonths),
                razorpayOrderId: response.razorpay_order_id,
                addonIds: validSelectedAddons.map((a) => a._id),
                subUserCount,
                addons: validSelectedAddons.map((a) => {
                  const activationValue = getAddonActivationValue(a, subUserCount, planMonths);

                  return {
                    name: a.name,
                    code: a.code,
                    value: activationValue,
                    price: isVerifiedListingAddon(a) ? getVerifyListingPack().amount : a.price
                  };
                }).filter((addon) => addon.value !== null),
                AddonsItems: validSelectedAddons
                  .filter((a) => {
                    const activationValue = getAddonActivationValue(a, subUserCount, planMonths);
                    return activationValue !== null;
                  })
                  .map((a) => {
                    const planMonthsValue = Number(selectedPricing?.durationMonths || 1);
                    const activationValue = getAddonActivationValue(a, subUserCount, planMonths);
                    const addonAmount = getAddonLineAmount(a, subUserCount, planMonthsValue);

                    return {
                      type: "Addon",
                      name: a.name,
                      value: activationValue.toString(),
                      amount: addonAmount.toString()
                    };
                  }),
                VerifyListing: verifyListingItems,
              };

              const activationResult = await apiClient(
                "post",
                "/ijscui/probroker-admin/ceknvuhbd/PremiumPlan/protected/plans/activate",
                activationPayload,
                { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
              );

              if (activationResult.success) {
                const validAccessItems = [
                  ...(Array.isArray(selectedPlan?.features) ? selectedPlan.features : []),
                  ...validSelectedAddons.map((addon) => addon.name).filter(Boolean),
                ];

                const baseStaffLimit = Number(selectedPlan?.subUserLimit || 0);
                const extraStaff = Math.max(totalSubUserLimit - baseStaffLimit, 0);

                // The server decides whether the offer applied; the quote only
                // tells us which festival theme to celebrate with.
                const verifiedAccount = verifyResponse.data?.data || {};
                const activatedAccount = activationResult.data?.data || {};
                const appliedOffer = verifiedAccount.appliedOffer || activatedAccount.appliedOffer || null;

                setPaymentCelebration({
                  appliedOffer,
                  expiredDate: verifiedAccount.expiredDate || activatedAccount.expiredDate || null,
                  isPending: Boolean(verifiedAccount.isPending ?? activatedAccount.isPending),
                  offerDisplay: offerQuote?.offer?.display || {
                    ...(findActiveOffer(selectedPricing?.offer)?.display || {}),
                    theme: selectedPricing?.offer?.theme,
                  },
                  amount: finalTotal,
                  planName: selectedPlan.planName,
                  durationInMonth: selectedPricing.durationMonths,
                  includedStaff: baseStaffLimit,
                  extraStaff,
                  totalStaff: totalSubUserLimit,
                  accessItems: validAccessItems,
                  addonItems: validSelectedAddons
                    .filter((addon) => !isSubUserAddon(addon))
                    .map((addon) => addon.name),
                });
                // Meta: money actually collected -> /subscription/success
                trackPurchase({
                  planName: selectedPlan?.planName,
                  planId: selectedPlan?._id,
                  months: selectedPricing?.durationMonths,
                  amount: finalTotal,
                });
                setPaymentProgress(null);
                setView("success");
              } else {
                failWith(activationResult.error?.message || "Activation did not complete.", verifyResponse);
              }
            } else {
              failWith(verifyResponse.data?.message || "Payment verification did not complete.");
            }
          } catch (err) {
            console.error("Payment finalization error:", err);
            failWith("Something went wrong while activating your plan.");
          }
      }

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        trackPaymentFailed({
          planName: selectedPlan?.planName,
          amount: finalTotal,
          reason: response?.error?.description || response?.error?.reason,
        });
      });
      rzp.open();
    } catch (error) {
      console.error("Payment Error:", error);
      toast.error("Something went wrong with the payment process");
    }
  };

  const getPopularIndex = () => {
    const kingIdx = plans.findIndex((p) => p.planName.toLowerCase().includes("king"));
    if (kingIdx !== -1) return kingIdx;
    return Math.floor(plans.length / 2);
  };

  const getOriginalPrice = (price) => Math.round(price * 1.2);
  const formatRoundedAmount = (amount) => Math.round(Number(amount) || 0).toLocaleString("en-IN");

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#503691]"></div>
      </div>
    );
  }

  const renderSelection = () => {
    return (
      <div className="max-w-7xl mx-auto space-y-10">
        {activeOffers[0] && (
          <OfferBanner
            key={`${activeOffers[0].slug}-${activeOffers[0].endAt}`}
            offer={activeOffers[0]}
            clockOffsetMs={offerClockOffset}
            onCta={handleOfferBannerCta}
          />
        )}

        <div className="text-center pt-2">
          <h2 className="text-4xl font-black text-gray-900 ">
            Choose Your Premium Plan
          </h2>
          <p className="text-gray-500 mt-3 text-lg font-medium">
            Unlock exclusive features and grow your business with PRObroker
          </p>
        </div>

        {/* Current Plan Header */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 bg-[#503691]/10 rounded-xl flex items-center justify-center text-[#503691]">
              <CreditCard size={28} />
            </div>
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase ">Current Plan</p>
              <h3 className="text-xl font-bold text-gray-900 mt-0.5">
                {currentPlanName}
              </h3>
              {currentPlanExpiryDate && (
                <p className="text-sm text-gray-500 mt-0.5">
                  {isDemoAccount ? "Demo expires:" : "Expires:"} {new Date(`${currentPlanExpiryDate}T00:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              )}
              {!isDemoAccount && Number(activePlan?.appliedOffer?.bonusMonths) > 0 && (
                <span className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                  <span aria-hidden="true">🎁</span>
                  Includes {formatBonusMonths(activePlan.appliedOffer.bonusMonths)}
                  {activePlan.appliedOffer.name ? ` (${activePlan.appliedOffer.name})` : ""}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <button
              onClick={handleViewMyPlanClick}
              className="flex-1 md:flex-none px-6 py-2.5 bg-[#503691] hover:bg-[#503691]/80 text-white font-bold rounded-xl shadow-lg shadow-[#503691]/20 transition-all flex items-center justify-center gap-2 text-sm"
            >
              <History size={18} />
              View My All Plan
            </button>
            
          </div>
        </div>

        {/* Tab Selection */}
        <div className="flex flex-col items-center space-y-8">
          <div className="bg-white p-1.5 rounded-2xl border border-gray-200 shadow-sm flex items-center gap-1">
            <button
              onClick={() => setActiveTab("plans")}
              className={`px-8 py-3 rounded-xl font-bold transition-all flex items-center gap-2 ${activeTab === "plans"
                  ? "bg-[#503691] text-white shadow-lg shadow-[#503691]/20"
                  : "text-gray-500 hover:text-gray-900"
                }`}
            >
              <Zap size={18} />
              Plans
            </button>
            <button
              onClick={handleSelectAddonsTab}
              className={`px-8 py-3 rounded-xl font-bold transition-all flex items-center gap-2 ${activeTab === "addons"
                  ? "bg-[#503691] text-white shadow-lg shadow-[#503691]/20"
                  : "text-gray-500 hover:text-gray-900"
                }`}
            >
              <ShoppingCart size={18} />
              Features
            </button>
          </div>
        </div>

        {activeTab === "plans" ? (
          <div id="subscription-plan-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 scroll-mt-4">
            {orderedPlans.map((plan) => {
              const primaryPricing = plan.pricing?.[0];
              const planDurationMonths = Number(primaryPricing?.durationMonths || 1);
              const planMonthlyPrice = Number(primaryPricing?.price || 0) / planDurationMonths;
              const isCurrentPlan = Boolean(activePlanCardId) && String(plan?._id || "") === activePlanCardId;
              const demo = isDemoPlan(plan);
              const displayName = demo ? "Free Plan" : plan.planName;
              // Verified Listings ships as a plan add-on, not a plan feature, so surface
              // it in the card list only for plans the add-on is attached to.
              const planHasVerifiedListing =
                (plan.addons || []).some((addon) => isVerifiedListingAddon(addon)) &&
                !(plan.features || []).some(
                  (feature) => normalizeAddonCode(feature).replace(/[_\s]/g, "").includes("verifiedlisting")
                );
              const planOfferRows = demo ? [] : (plan.pricing || []).filter((row) => row?.offer);
              const planOfferTheme = planOfferRows.length > 0 ? getPricingOfferTheme(planOfferRows[0].offer) : null;

              return (
                <div
                  key={plan._id}
                  className={`relative rounded-3xl border-2 p-6 transition-all duration-300 flex flex-col ${demo
                      ? "bg-gray-50 border-gray-200 text-gray-500 shadow-sm"
                      : `bg-white ${selectedPlan?._id === plan._id && !demo
                        ? "border-[#503691] shadow-xl shadow-[#503691]/20"
                        : "border-gray-100 hover:border-[#503691]/20 shadow-sm"
                      }`
                    }`}
                >
                  {/* Plan Content */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className={`text-xl font-bold mt-0.5 capitalize ${demo ? 'text-gray-700' : 'text-gray-900'}`}>
                        {displayName}
                      </h3>
                      <div className="flex items-center gap-2">
                        {isCurrentPlan && (
                          <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-widest rounded-full">
                            Active
                          </span>
                        )}
                        
                      </div>
                    </div>
                    <p className="text-gray-500 font-medium text-sm leading-relaxed h-10">
                      {plan.description || `Perfect solution for growing ${plan.planName} users`}
                    </p>
                  </div>

                  {planOfferTheme && (
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <OfferBadge
                        theme={planOfferTheme}
                        text={planOfferRows[0].offer.badgeText || `+${formatMonths(planOfferRows[0].offer.bonusMonths)} FREE`}
                      />
                      <span className="text-xs font-semibold text-gray-600">
                        on {joinDurations(planOfferRows)}-month plans
                      </span>
                    </div>
                  )}

                  {/* Single Price Display */}
                  <div className="mb-6 p-4 rounded-2xl border border-violet-100 bg-violet-50/40">
                    <div className="flex items-end gap-2">
                      <span className="text-2xl font-bold text-black">
                        ₹{Number(planMonthlyPrice).toFixed(2)}
                      </span>
                      <span className="text-gray-500 font-bold text-sm">
                        /mo
                      </span>
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="space-y-3 mb-6 flex-1">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3 group">
                        <div className="w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-100 transition-colors">
                          <Check size={12} className="text-emerald-600" strokeWidth={4} />
                        </div>
                        <span className="text-gray-600 font-bold text-[13px] lowercase first-letter:uppercase">
                          {feature.replace(/_/g, " ")}
                        </span>
                      </div>
                    ))}
                    {planHasVerifiedListing && (
                      <div className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded-full bg-violet-50 flex items-center justify-center flex-shrink-0">
                          <MapPin size={12} className="text-[#503691]" />
                        </div>
                        <span className="text-gray-600 font-bold text-[13px]">
                          Verify Listing
                        </span>
                      </div>
                    )}
                    {/* Additional stats */}
                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Shield size={12} className="text-blue-600" />
                      </div>
                      <span className="text-gray-600 font-bold text-[13px]">
                        {plan.dailyContactLimit} Daily Contact Limit
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setSelectedAddons([]);
                      setAppliedCheckoutCoupon(null);
                      setCheckoutCoupon("");
                      if (!demo) handleChoosePlan(plan);
                    }}
                    disabled={isCurrentPlan || !primaryPricing || demo}
                    className={`w-full py-3 rounded-xl font-black transition-all text-sm tracking-wide ${isCurrentPlan || !primaryPricing || demo
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-[#503691] text-white shadow-lg shadow-[#503691]/20 hover:bg-[#503691]/80"
                    }`}
                  >
                    {demo ? "Free Plan" : (isCurrentPlan ? "Current Plan" : "Choose Plan")}
                  </button>
                </div>
              );
            })}
          </div>
        ) : activeTab === "addons" ? (
          <Addons
            userId={userId}
            token={token}
            activePlan={activePlan}
            isDemoAccount={isDemoAccount}
            fallbackPaidAccountId={latestPaidAccount?._id}
            fallbackSubUserLimit={userProfile?.user?.subUserLimit || userProfile?.subUserLimit || 0}
            paidAddonsData={userProfile?.paidAddonsData || []}
            onPaymentSuccess={(paymentSummary) => {
              setPaymentCelebration(paymentSummary || null);
              setHistoryTab("addons");
              setView("history");
              if (userId) {
                fetchUserProfile(userId, true).catch((error) => {
                  console.warn("Addon payment refresh failed:", error?.message);
                });
              }
            }}
          />
        ) : null}
      </div>
    );
  };

  const renderContent = () => {
    if (view === "history") {
      return (
        <div className="max-w-5xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setView("plans")}
              className="flex items-center gap-2 text-gray-500 hover:text-[#503691] font-bold transition-all"
            >
              <ArrowRight size={18} className="rotate-180" />
              Back to Plans
            </button>
            <h2 className="text-2xl font-black text-gray-900">Payment History</h2>
          </div>
          <div className="flex justify-center">
            <div className="bg-white p-1.5 rounded-2xl border border-gray-200 shadow-sm flex items-center gap-1">
              <button
                onClick={() => handleHistoryTabChange("subscriptions")}
                className={`px-6 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 text-sm ${historyTab === "subscriptions"
                    ? "bg-[#503691] text-white shadow-lg shadow-violet-200"
                    : "text-gray-500 hover:text-gray-900"
                  }`}
              >
                <History size={16} />
                My Subscriptions
              </button>
              <button
                onClick={() => handleHistoryTabChange("addons")}
                className={`px-6 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 text-sm ${historyTab === "addons"
                    ? "bg-[#503691] text-white shadow-lg shadow-violet-200"
                    : "text-gray-500 hover:text-gray-900"
                  }`}
              >
                <ShoppingCart size={16} />
                My Features
              </button>
            </div>
          </div>
          <MyPlans
            history={historyTab === "subscriptions" ? mappedSubscriptionHistory : (userProfile?.paidAddonsData || [])}
            featuresAccessData={userProfile?.FeaturesAccessData || []}
            paidAddonsData={userProfile?.paidAddonsData || []}
            mode={historyTab === "subscriptions" ? "subscriptions" : "addons"}
            loading={historyTab === "addons" ? loadingProfile : false}
            user={userProfile?.user}
          />
        </div>
      );
    }

    if (view === "checkout" && selectedPlan && selectedPricing) {
      // Re-calculate checkout values
      const TAX_RATE = 0.18;
      const basePrice = selectedPricing?.price || 0;
      const checkoutPricingOptions = (selectedPlan?.pricing || [])
        .slice()
        .sort((a, b) => Number(a?.durationMonths || 0) - Number(b?.durationMonths || 0));
      const validSelectedAddons = selectedAddons.filter((addon) => isPlanAddonAllowed(addon?.code));
      const planMonths = Number(selectedPricing?.durationMonths || 1);

      // Offer display. The bonus line and access length come only from the
      // /offers/quote response; the listing's row summary drives the badge.
      const pricingOffer = selectedPricing?.offer || null;
      const offerBonus =
        offerQuote?.eligible && Number(offerQuote?.bonusMonths) > 0 ? offerQuote : null;
      const offerTheme = offerBonus?.offer?.display
        ? resolveOfferTheme(offerBonus.offer.display)
        : getPricingOfferTheme(pricingOffer);
      const offerLabels = getOfferLabels(offerTheme);
      const planOfferRows = checkoutPricingOptions.filter((pricing) => pricing?.offer);
      const bestValuePricingId = planOfferRows.length > 1
        ? planOfferRows.reduce((best, row) =>
          Number(row.price) / Number(row.offer.totalMonths || 1) <
            Number(best.price) / Number(best.offer.totalMonths || 1)
            ? row
            : best
        )._id
        : null;
      // The quote turned the offer down because of the applied promo code.
      const showOfferNoStackNote = Boolean(
        appliedCheckoutCoupon && pricingOffer?.stackWithPromo === false && offerQuote && !offerQuote.eligible
      );
      // Before any code is applied: warn that a code would cost the bonus.
      const showOfferNoStackHint = Boolean(
        !appliedCheckoutCoupon && pricingOffer?.stackWithPromo === false && offerBonus
      );
      const pricingOfferBadgeText =
        pricingOffer?.badgeText || (pricingOffer ? `+${formatMonths(pricingOffer.bonusMonths)} FREE` : "");
      const removeCheckoutCoupon = () => {
        setAppliedCheckoutCoupon(null);
        setCheckoutCoupon("");
        toast.info("Coupon removed");
      };
      const pricingOfferSource = findActiveOffer(pricingOffer);
      // Display estimate only (labelled approx.): the payload keeps the paid
      // term and the server adds the bonus, queueing after any current plan.
      const accessMonths = offerBonus ? Number(offerBonus.totalMonths) : planMonths;
      const checkoutExpiredDate = calculateExpiredDate(planStartDate, accessMonths);
      const formatShortDate = (date) =>
        date ? date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";

      const addonPrice = calculateAddonTotal(validSelectedAddons, subUserCount, planMonths);
      let discountAmount = 0;
      if (appliedCheckoutCoupon) {
        if (appliedCheckoutCoupon.discountType === "percentage") {
          discountAmount = (basePrice + addonPrice) * appliedCheckoutCoupon.discountValue / 100;
        } else {
          discountAmount = appliedCheckoutCoupon.discountValue;
        }
      }
      const subtotalAfterDiscount = basePrice - discountAmount + addonPrice;
      const taxAmount = subtotalAfterDiscount * TAX_RATE;
      const finalTotalAmount = Number(Math.max(subtotalAfterDiscount + taxAmount , 0));
      const checkoutAddons = (selectedPlan?.addons || [])
        .filter((addon) => isPlanAddonAllowed(addon?.code))
        .sort((a, b) => Number(isVerifiedListingAddon(b)) - Number(isVerifiedListingAddon(a)));
      const currentPlanExpiryText = currentPlanExpiryDate
        ? new Date(`${currentPlanExpiryDate}T00:00:00`).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric"
        })
        : "";

      // Only show the expiry notice when the profile response contains PlanExpriDate
      const showProfileExpiryNotice = Boolean(
        userProfile?.PlanExpriDate || userProfile?.user?.PlanExpriDate
      );
      const newPlanStartText = planStartDate
        ? new Date(`${planStartDate}T00:00:00`).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric"
        })
        : "";

      return (
        <div className="max-w-[1100px] mx-auto flex flex-col md:flex-row gap-6">
          {/* Existing Checkout JSX remains here, but we'll return it inside renderContent */}
          <div className="flex-[2]">
            <span className="inline-block mb-4 text-gray-500 cursor-pointer font-semibold hover:text-gray-900" onClick={() => setView("plans")}>← Back to plans</span>
            <h1 className="text-[1.75rem] font-extrabold mb-8 text-gray-900">Your Cart</h1>
            <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="bg-gray-100 p-2.5 rounded-lg"><ShoppingCart size={24} color="#374151" /></div>
                <h2 className="text-xl font-bold text-gray-900">{selectedPlan?.planName}</h2>
              </div>
              <div className="mt-4">
                <label className="text-[0.9rem] font-semibold text-gray-700 mb-2 block">Duration</label>
                <select
                  value={selectedPricing?._id || ""}
                  onChange={(e) => {
                    const nextPricing = checkoutPricingOptions.find((pricing) => pricing._id === e.target.value);
                    if (nextPricing) {
                      setSelectedPricing(nextPricing);
                    }
                  }}
                  className="w-full p-3 px-4 border border-gray-200 rounded-lg text-base text-gray-900 bg-white font-semibold outline-none focus:border-violet-500"
                >
                  {checkoutPricingOptions.map((pricing) => {
                    const months = Number(pricing?.durationMonths || 0);

                    return (
                      <option key={pricing._id} value={pricing._id}>
                        {months} months
                        {pricing.offer ? ` + ${formatMonths(pricing.offer.bonusMonths)} FREE` : ""}
                      </option>
                    );
                  })}
                </select>
                {pricingOffer ? (
                  <PricingOfferDetails
                    pricing={selectedPricing}
                    theme={offerTheme}
                    isBestValue={selectedPricing._id === bestValuePricingId}
                    onOpenTerms={() => setIsOfferTermsOpen(true)}
                    blockedPromoCode={showOfferNoStackNote ? appliedCheckoutCoupon.code : ""}
                    onRemovePromo={removeCheckoutCoupon}
                  />
                ) : planOfferRows.length > 0 ? (
                  <p className="mt-2 text-[0.82rem] font-semibold text-gray-600">
                    Choose {joinDurations(planOfferRows, "or")} months to get{" "}
                    {planOfferRows[0].offer.badgeText || `+${formatMonths(planOfferRows[0].offer.bonusMonths)} FREE`}
                  </p>
                ) : null}
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[0.82rem] font-semibold text-gray-700 mb-1 block">Start Date</label>
                    <input
                      type="date"
                      value={planStartDate}
                      min={minSelectableStartDate}
                      onFocus={() => {
                        if (!planStartDate || planStartDate < minSelectableStartDate) {
                          setPlanStartDate(minSelectableStartDate);
                        }
                      }}
                      onClick={() => {
                        if (!planStartDate || planStartDate < minSelectableStartDate) {
                          setPlanStartDate(minSelectableStartDate);
                        }
                      }}
                      onChange={(e) => {
                        const selectedDate = e.target.value;

                        // User can only start from the day after active plan expiry.
                        if (selectedDate < minSelectableStartDate) {
                          const expiryFormatted = activePlanExpiryDate
                            ? new Date(`${activePlanExpiryDate}T00:00:00`).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric"
                            })
                            : new Date(minSelectableStartDate).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric"
                            });

                          toast.warning(
                            `Your plan is currently active and expires on ${expiryFormatted}. Please select a date after that.`,
                            { autoClose: 4000 }
                          );
                          return;
                        }

                        setPlanStartDate(selectedDate);
                      }}
                      className="w-full p-2.5 px-3 border border-gray-200 rounded-lg text-sm text-gray-900 bg-white font-semibold outline-none focus:border-violet-500"
                    />
                  </div>
                  <div>
                    <label className="text-[0.82rem] font-semibold text-gray-700 mb-1 block">
                      Expires On{offerBonus ? " (approx.)" : ""}
                    </label>
                    <div className="w-full p-2.5 px-3 border border-gray-200 rounded-lg text-sm text-gray-700 bg-gray-50 font-semibold">
                      {formatShortDate(checkoutExpiredDate)}
                    </div>
                    {offerBonus && (
                      <p className="mt-1 text-[0.75rem] font-semibold text-emerald-700">
                        Includes +{formatMonths(offerBonus.bonusMonths)} FREE
                      </p>
                    )}
                  </div>
                </div>
                {showProfileExpiryNotice && currentPlanExpiryText && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                    Select any date after <strong>{currentPlanExpiryText}</strong> to activate your new plan.
                  </div>
                )}
                <div className="text-right mt-3">
                  <div className="text-xl font-bold text-gray-900">₹{Number(basePrice / planMonths).toFixed(2)}/mo</div>
                  {/* <div className="text-[0.85rem] text-gray-500 line-through">₹{formatRoundedAmount(getOriginalPrice(basePrice) / selectedPricing.durationMonths)}/mo</div> */}
                </div>
              </div>

            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-violet-50 p-2.5 rounded-lg"><CreditCard size={24} color="#503691" /></div>
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">Billing Details</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                <div>
                  <label className="text-[0.82rem] font-semibold text-gray-700 mb-1 block">Billing Name <span className="text-rose-500">*</span></label>
                  <input
                    type="text"
                    value={billingName}
                    placeholder="Billing Name"
                    onChange={(e) => setBillingName(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-white text-sm text-gray-900 font-normal outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="text-[0.82rem] font-semibold text-gray-700 mb-1 block">GST Number <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={gstNumber}
                    placeholder="GST Number"
                    onChange={(e) => setGstNumber(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-white text-sm text-gray-900 font-normal outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="text-[0.82rem] font-semibold text-gray-700 mb-1 block">Billing Email <span className="text-rose-500">*</span></label>
                  <input
                    type="email"
                    value={billingEmail}
                    placeholder="Billing Email"
                    onChange={(e) => setBillingEmail(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-white text-sm text-gray-900 font-normal outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="text-[0.82rem] font-semibold text-gray-700 mb-1 block">Billing State <span className="text-rose-500">*</span></label>
                  {/* Fall back to free text if the state list couldn't be loaded, so checkout is never blocked. */}
                  {billingStateOptions.length > 0 ? (
                    <select
                      value={billingState}
                      onChange={(e) => setBillingState(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-white text-sm text-gray-900 font-normal outline-none focus:border-violet-500"
                    >
                      <option value="">Select State</option>
                      {billingStateOptions.map((stateName) => (
                        <option key={stateName} value={stateName}>{stateName}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={billingState}
                      placeholder="Billing State"
                      onChange={(e) => setBillingState(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-white text-sm text-gray-900 font-normal outline-none focus:border-violet-500"
                    />
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[0.82rem] font-semibold text-gray-700 mb-1 block">Billing Address <span className="text-rose-500">*</span></label>
                  <textarea
                    value={billingAddress}
                    placeholder="Billing Address"
                    rows={2}
                    onChange={(e) => setBillingAddress(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-white text-sm text-gray-900 font-normal outline-none focus:border-violet-500 resize-none"
                  />
                </div>
              </div>
            </div>
            {checkoutAddons.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
                <div className="flex items-center gap-3 mb-2"><div className="bg-yellow-50 p-2.5 rounded-lg"><Star size={24} color="#ca8a04" /></div><h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">Enhance with Add-ons<span className="text-[0.72rem] font-bold bg-violet-100 text-[#503691] px-2.5 py-0.5 rounded-full tracking-wide">OPTIONAL</span></h2></div>
                <div className="flex flex-col gap-3 mt-5">
                  {checkoutAddons.map((addon) => {
                    const isSubUser = isSubUserAddon(addon);
                    const isVerifiedListing = isVerifiedListingAddon(addon);
                    const isSelected = selectedAddons.some((a) => a._id === addon._id);
                    const totalUsers = selectedPlan.subUserLimit + subUserCount;
                    const verifyListingPack = getVerifyListingPack();
                    const addonCost = isSubUser && isSelected
                      ? subUserCount * addon.price
                      : isVerifiedListing
                        ? verifyListingPack.amount
                        : addon.price * Number(selectedPricing?.durationMonths || 1);

                    return (
                      <div
                        key={addon._id}
                        className={`rounded-[14px] border-2 overflow-hidden transition-all duration-200 cursor-pointer relative ${isSelected ? "border-[#503691] bg-violet-50" : "border-gray-200 bg-white"}`}
                        onClick={() => handleAddonToggle(addon)}
                      >
                        {isSelected && <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#503691] to-[#503691]/50" />}
                        <div className="flex items-center gap-3.5 py-[18px] px-[18px] pl-5">
                          <div className={`w-[22px] h-[22px] min-w-[22px] rounded-md border-2 flex items-center justify-center shrink-0 transition-all duration-200 ${isSelected ? "border-[#503691] bg-[#503691]" : "border-gray-300 bg-white"}`}>{isSelected && <Check size={12} color="white" strokeWidth={3} />}</div>
                          <div className={`w-11 h-11 min-w-[44px] rounded-xl flex items-center justify-center text-[22px] ${isSelected ? "bg-[#503691]/10" : "bg-[#503691]/5"}`}>{isSubUser ? "👥" : isVerifiedListing ? <MapPin size={22} color="#503691" strokeWidth={2} /> : "⭐"}</div>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-gray-900 text-[15px]">{addon.name}</div>
                            <div className="text-[13px] text-gray-500 mt-0.5 leading-normal">
                              {isSubUser
                                ? <><strong>{selectedPlan.subUserLimit} sub-users</strong> are included in your plan. Add more at ₹{addon.price.toLocaleString()} per user.</>
                                : isVerifiedListing
                                  ? addon.description || "Get your listings verified. Choose a credit pack below."
                                  : addon.description || `Enable ${addon.name} for this plan.`}
                            </div>
                          </div>
                          <div className="text-right min-w-[80px] shrink-0"><div className="text-[17px] font-extrabold text-[#503691]">₹{formatRoundedAmount(addonCost)}</div><div className="text-[11px] text-gray-500 mt-0.5">{isSubUser ? "per user" : isVerifiedListing ? verifyListingPack.label : "one time"}</div></div>
                        </div>
                        {isSelected && isVerifiedListing && (
                          <div onClick={(e) => e.stopPropagation()} className="mx-4 mb-4 ml-5 bg-slate-50 rounded-xl p-4 border border-slate-200">
                            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-3">Choose a credit pack</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {Object.entries(VERIFY_LISTING_PRICING).map(([packCode, pack]) => {
                                const isActivePack = verifyListingPlanCode === packCode;

                                return (
                                  <button
                                    key={packCode}
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setVerifyListingPlanCode(packCode); }}
                                    className={`rounded-xl border-2 px-4 py-3 text-center transition-all duration-200 ${isActivePack ? "border-[#503691] bg-white shadow-sm" : "border-gray-200 bg-white hover:border-[#503691]/40"}`}
                                  >
                                    <div className="text-[13px] font-bold text-gray-700">{pack.label}</div>
                                    <div className="text-xl font-extrabold text-[#503691] mt-0.5">₹{pack.amount.toLocaleString()}</div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {isSelected && isSubUser && (
                          <div onClick={(e) => e.stopPropagation()} className="mx-4 mb-4 ml-5 bg-slate-50 rounded-xl p-4 border border-slate-200">
                            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-3">Extra sub-users to add</div>
                            <div className="flex items-center gap-4 flex-wrap">
                              <div className="flex items-center bg-slate-100 rounded-xl p-1 gap-0.5">
                                <button onClick={(e) => { e.stopPropagation(); setSubUserCount(Math.max(1, subUserCount - 1)); }} className="w-9 h-9 flex items-center justify-center bg-white border-none rounded-lg cursor-pointer shadow-sm text-[#503691] font-extrabold text-lg">−</button>
                                <input type="number" min="1" value={subUserCount} onChange={(e) => { e.stopPropagation(); setSubUserCount(Math.max(1, parseInt(e.target.value) || 1)); }} onClick={(e) => e.stopPropagation()} className="w-12 h-9 border-none bg-transparent text-center font-extrabold text-base text-slate-800 outline-none" />
                                <button onClick={(e) => { e.stopPropagation(); setSubUserCount(subUserCount + 1); }} className="w-9 h-9 flex items-center justify-center bg-[#503691] border-none rounded-lg cursor-pointer text-white font-extrabold text-lg">+</button>
                              </div>
                              <div className="mt-3.5 px-8 py-3.5 bg-[#503691]/[0.06] rounded-[10px] flex justify-between items-center flex-wrap gap-2.5">
                                <div><div className="text-xs font-semibold text-gray-700 mb-1">Sub-user breakdown</div><div className="text-[13px]"><span className="text-emerald-600 font-semibold">✓ {selectedPlan.subUserLimit} included in plan</span><span className="text-gray-500"> + </span><span className="text-[#503691] font-semibold">+{subUserCount} extra</span><span className="text-gray-500"> = </span><strong className="text-gray-900">{totalUsers} total</strong></div></div>
                                <div className="text-right"><div className="text-[11px] text-gray-500">Add-on cost</div><div className="font-extrabold text-xl text-[#503691]">₹{formatRoundedAmount(subUserCount * addon.price)}</div></div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="flex-[1]">
  <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
    
    {/* Title */}
    <h2 className="text-xl font-bold text-gray-900 mb-5">
      Order summary
    </h2>

    {/* Base Price */}
    <div className="flex justify-between mb-2 text-[0.95rem] text-gray-600">
      <span className="font-medium">
        Base Price ({selectedPricing.durationMonths}-month)
      </span>
      <span className="text-gray-900 font-semibold">
        ₹{Number(basePrice).toFixed(2)}
      </span>
    </div>

    {/* Offer bonus (from /offers/quote) */}
    {offerBonus && (
      <div className="flex justify-between gap-3 mb-2 text-[0.95rem] text-emerald-700 font-semibold">
        <span>
          <span aria-hidden="true">🎁 </span>
          {offerLabels.bonusLabel}: +{formatMonths(offerBonus.bonusMonths)}
        </span>
        <span className="shrink-0">FREE</span>
      </div>
    )}
    {showOfferNoStackNote && (
      <div className="flex justify-between gap-3 mb-2 text-[0.95rem] text-gray-400">
        <span className="line-through">
          <span aria-hidden="true">🎁 </span>
          {offerLabels.bonusLabel}: +{formatMonths(pricingOffer.bonusMonths)}
        </span>
        <span className="shrink-0 text-[0.8rem] font-semibold text-amber-700">Not applied</span>
      </div>
    )}

    {/* Addons */}
    {addonPrice > 0 && (
      <div className="flex flex-col gap-2 mb-3">
        {selectedAddons.map((addon) => {
          const isSubUserAddonItem = isSubUserAddon(addon);
          const isVerifiedListingItem = isVerifiedListingAddon(addon);
          const planMonths = Number(selectedPricing?.durationMonths || 1);

          const displayPrice = getAddonLineAmount(addon, subUserCount, planMonths);

          return (
            <div
              key={addon._id}
              className="flex justify-between items-center text-[0.95rem]"
            >
              <div className="flex items-center gap-2 text-gray-600">
                <div className="w-2 h-2 rounded-full bg-[#503691] shrink-0" />
                <span>
                  {isSubUserAddonItem
                    ? `👥 Sub Users (+${subUserCount} extra = ${
                        selectedPlan.subUserLimit + subUserCount
                      } total)`
                    : isVerifiedListingItem
                      ? `${addon.name} (${getVerifyListingPack().label})`
                      : addon.name}
                </span>
              </div>

              <span className="font-semibold text-gray-900">
                ₹{formatRoundedAmount(displayPrice)}
              </span>
            </div>
          );
        })}
      </div>
    )}
    <hr />

    {/* Discount */}
    {appliedCheckoutCoupon && (
      <div className="flex justify-between mb-2  mt-2 text-[0.95rem] text-emerald-600 font-semibold">
        <span>
           Discount ({appliedCheckoutCoupon.discountValue}
          {appliedCheckoutCoupon.discountType === "percentage" ? "%" : " Flat"})
        </span>
        <span>- ₹{(discountAmount)}</span>
      </div>
    )}

    {/* Subtotal */}
    <div className="flex justify-between mb-2  text-[0.95rem] text-gray-600">
      <span className="font-medium">
        {appliedCheckoutCoupon ? "Subtotal" : " Subtotal"}
      </span>
      <span className="text-gray-900 font-semibold">
        ₹{Number(subtotalAfterDiscount).toFixed(2)}
      </span>
    </div>

     {/* GST */}
    <div className="flex justify-between mb-3 text-[0.95rem] text-gray-600">
      <div className="flex items-center gap-1">
        <span className="font-medium">
          {appliedCheckoutCoupon ? "GST (18%)" : "GST (18%)"}
        </span>
        <Info size={14} color="#9ca3af" />
      </div>
      <span className="text-gray-900 font-semibold">
        ₹{Number(taxAmount).toFixed(2)}
      </span>
    </div>

    {/* Total */}
    <div className="flex justify-between  items-end mt-5 pt-5 border-t border-dashed border-slate-200">
      <span className="text-[1.2rem] font-bold text-gray-900">
        {appliedCheckoutCoupon ? " Total" : "Total"}
      </span>

      <div className="text-right">
        <div className="text-[1.75rem] font-extrabold text-[#503691]">
          ₹{Number(finalTotalAmount).toFixed(2)}
        </div>
      </div>
    </div>

    {offerBonus && (
      <p className="mt-2 text-[0.85rem] font-semibold text-gray-700">
        Access: {formatMonths(accessMonths)} · valid till ~{formatShortDate(checkoutExpiredDate)}{" "}
        <span className="font-medium text-gray-500">(approx.)</span>
      </p>
    )}

    {/* Coupon Section */}
    <div className="mt-6">
      {appliedCheckoutCoupon ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex justify-between items-center">
          
          <div className="flex items-center gap-3">
            <div className="bg-emerald-100 p-2 rounded-full">
              <Check size={16} className="text-emerald-600" />
            </div>

            <div>
              <div className="text-[0.7rem] text-emerald-600 font-bold uppercase tracking-wider mb-1">
                Coupon Applied
              </div>
              <div className="text-[0.95rem] font-bold text-emerald-700">
                {appliedCheckoutCoupon.code}
              </div>
            </div>
          </div>

          <button
            onClick={removeCheckoutCoupon}
            aria-label="Remove coupon"
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
          >
            <X size={18} />
          </button>
        </div>
      ) : (
        <>
          <span className="text-[#503691] font-semibold cursor-pointer text-[0.95rem]">
            Your cartcoupon code?
          </span>

          <div className="flex gap-2 mt-3">
            <input
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 outline-none focus:border-[#503691] transition-colors"
              placeholder="Enter code"
              value={checkoutCoupon}
              onChange={(e) => setCheckoutCoupon(e.target.value)}
            />

            <button
              className="bg-white border border-[#503691] text-[#503691] rounded-lg px-4 py-2 font-bold hover:bg-[#503691]/10 transition-colors"
              onClick={handleApplyCoupon}
            >
              Apply
            </button>
          </div>
        </>
      )}

      {showOfferNoStackNote && (
        <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 p-3" role="status">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-700" aria-hidden="true" />
          <div className="min-w-0 text-[0.82rem] leading-relaxed text-amber-900">
            <p className="font-bold">{offerLabels.noStackNote}</p>
            <p className="mt-0.5">
              You get the {appliedCheckoutCoupon.code} discount and {formatMonths(planMonths)} of access.
            </p>
            <button
              type="button"
              onClick={removeCheckoutCoupon}
              className="mt-1.5 font-bold text-[#503691] underline underline-offset-2 hover:text-[#503691]/80"
            >
              Remove code &amp; get {pricingOfferBadgeText} instead
            </button>
          </div>
        </div>
      )}

      {showOfferNoStackHint && (
        <p className="mt-2 flex items-start gap-1.5 text-[0.8rem] font-medium text-gray-600">
          <Info size={14} className="mt-0.5 shrink-0 text-gray-400" aria-hidden="true" />
          <span>
            Applying a promo code removes the {offerLabels.bonusLabel} ({pricingOfferBadgeText}).
          </span>
        </p>
      )}

      {pricingOffer && (
        <OfferTermsSheet
          open={isOfferTermsOpen}
          onOpenChange={setIsOfferTermsOpen}
          offerName={pricingOfferSource?.name || pricingOffer.name}
          termsText={pricingOfferSource?.display?.termsText}
          endAt={pricingOffer.endAt}
        />
      )}

      <div className="mt-4">
        <label className="text-[0.82rem] font-semibold text-gray-700 mb-1 block">
          GST NO
        </label>
        <input
          type="text"
          value={userGSTNo}
          placeholder="GST NO"
          onChange={(e) => setUserGSTNo(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-white text-sm text-gray-900 font-normal outline-none"
        />
      </div>
    </div>

    {/* Continue Button */}
    {(() => {
      const missingBillingFields = [
        !billingName.trim() && "Billing Name",
        !billingEmail.trim() && "Billing Email",
        !billingAddress.trim() && "Billing Address",
        !billingState.trim() && "Billing State",
      ].filter(Boolean);
      const hasMissingBillingFields = missingBillingFields.length > 0;

      return (
        <>
          <button
            disabled={hasMissingBillingFields}
            className={`w-full rounded-lg py-3.5 font-bold text-base mt-6 transition-colors ${
              hasMissingBillingFields
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-[#503691] text-white hover:bg-[#503691]/80"
            }`}
            onClick={handleActivatePlan}
          >
            Continue
          </button>

          {hasMissingBillingFields && (
            <p className="mt-2 text-center text-[0.82rem] font-bold  text-rose-500">
              Please fill up {missingBillingFields.join(", ")}
            </p>
          )}
        </>
      );
    })()}

  </div>
          </div>
        </div>
      );
    }

    return renderSelection();
  };

  return (
    <div className="font-['DM_Sans',sans-serif] min-h-screen  py-4 px-2 pb-2">
      <PaymentProcessingOverlay
        progress={paymentProgress}
        onRetry={() => paymentProgress?.retry?.()}
        onClose={() => {
          setPaymentProgress(null);
          handleViewMyPlanClick();
        }}
      />
      <PaymentCelebration
        isOpen={Boolean(paymentCelebration)}
        summary={paymentCelebration || {}}
        onClose={() => {
          setPaymentCelebration(null);
          // Leave /subscription/success and land back on the plans list.
          // `replace` so Back does not re-open the success screen. Other
          // screens that open this popup (addons) stay where they are.
          if (view === "success") setView("plans", { replace: true });
        }}
      />
      {renderContent()}
    </div>
  );
};

export default Subscription;
